import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const EMPTY = { tpl_id: null, title: "", text: "" };

export const TemplateManager = ({ open, onOpenChange, templates, reload }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const save = async () => {
    setBusy(true);
    try {
      if (f.tpl_id) await api.put(`/chat-admin/templates/${f.tpl_id}`, { title: f.title, text: f.text });
      else await api.post("/chat-admin/templates", { title: f.title, text: f.text });
      toast.success("Template disimpan");
      setF(EMPTY);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (confirmId !== id) {
      setConfirmId(id);
      return toast.info("Tekan ikon hapus sekali lagi untuk menghapus template");
    }
    setConfirmId(null);
    try {
      await api.delete(`/chat-admin/templates/${id}`);
      toast.success("Template dihapus");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="template-manager">
        <DialogHeader>
          <DialogTitle>Kelola Balasan Cepat</DialogTitle>
          <DialogDescription>Template dipakai bersama semua petugas. Tulis {"{nama}"} untuk menyisipkan nama anggota.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 bg-slate-50 rounded-xl p-3">
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Judul, contoh: Cara bayar angsuran" data-testid="template-title-input" />
          <Textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="Isi balasan" rows={4} data-testid="template-text-input" />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy || f.title.trim().length < 2 || f.text.trim().length < 2} data-testid="template-save"
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg">
              <Plus className="h-4 w-4" />{f.tpl_id ? "Simpan Perubahan" : "Tambah Template"}
            </button>
            {f.tpl_id && <button onClick={() => setF(EMPTY)} className="text-sm font-semibold text-slate-600 px-3" data-testid="template-cancel-edit">Batal</button>}
          </div>
        </div>
        <div className="space-y-2" data-testid="template-list">
          {templates?.map((t) => (
            <div key={t.tpl_id} className="flex items-start gap-2 border border-slate-100 rounded-xl p-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800">{t.title}</div>
                <div className="text-xs text-slate-500 whitespace-pre-wrap line-clamp-3">{t.text}</div>
              </div>
              <button onClick={() => setF(t)} data-testid={`template-edit-${t.tpl_id}`} className="p-1.5 text-slate-400 hover:text-blue-700" aria-label="Ubah"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(t.tpl_id)} data-testid={`template-delete-${t.tpl_id}`} className={`p-1.5 hover:text-red-600 ${confirmId === t.tpl_id ? "text-red-600 bg-red-50 rounded-lg" : "text-slate-400"}`} aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
